/**
 * Semantic mappers: domain enum -> BadgeTone.
 * Pure functions, kept separate from <Badge> so tone logic stays greppable.
 * Source of truth: `badges.md` at repo root.
 *
 * @typedef {import('./Badge').BadgeTone} BadgeTone
 */

/**
 * Case status — stored PascalCase: `'Open' | 'InProgress' | 'Closed'`.
 * @param {string} status
 * @returns {BadgeTone}
 */
export function caseStatusTone(status) {
  switch (status) {
    case 'Open':       return 'info';
    case 'InProgress': return 'warning';
    case 'Closed':     return 'neutral';
    default:           return 'neutral';
  }
}

/**
 * Alert status from the detection pipeline. Real values in code are
 * `'new' | 'acknowledged' | 'closed'`. `triaged/in_progress/resolved` in
 * `badges.md` are spec-future, not emitted by the pipeline today.
 * @param {string} status
 * @returns {BadgeTone}
 */
export function alertStatusTone(status) {
  switch (String(status || '').toLowerCase()) {
    case 'new':          return 'critical';
    case 'acknowledged': return 'warning';
    case 'closed':       return 'success';
    default:             return 'neutral';
  }
}

/**
 * Severity: `'critical' | 'high' | 'medium' | 'low' | 'informational'`.
 * `high` rolls up to `critical` tone per the spec mapping table.
 * @param {string} severity
 * @returns {BadgeTone}
 */
export function severityTone(severity) {
  switch (String(severity || '').toLowerCase()) {
    case 'critical':
    case 'high':
      return 'critical';
    case 'medium':        return 'warning';
    case 'low':           return 'info';
    case 'informational': return 'neutral';
    default:              return 'neutral';
  }
}

/**
 * Rule mode: `'PROD' | 'TEST'`.
 * @param {string} mode
 * @returns {BadgeTone}
 */
export function ruleModeTone(mode) {
  return mode === 'PROD' ? 'success' : 'neutral';
}

/**
 * Tenant state: `'active' | 'deleted' | 'inactive' | 'pending'`.
 * `pending` isn't in the spec mapping table but is used in code; mapped to warning.
 * @param {string} state
 * @returns {BadgeTone}
 */
export function tenantStateTone(state) {
  switch (String(state || '').toLowerCase()) {
    case 'active':   return 'success';
    case 'deleted':  return 'critical';
    case 'inactive': return 'neutral';
    case 'pending':  return 'warning';
    default:         return 'warning';
  }
}

/**
 * Microsoft cloud environment a tenant lives in: `'' | 'Commercial' | 'GCC' | 'GCCHigh' | 'DoD'`
 * (the `cloudEnvironment` column on `M365TenantServices.csv`). Blank means Commercial.
 * Matched case-insensitively; any unrecognised value falls open to Commercial/neutral rather
 * than erroring, per the log-collection contract.
 * @param {string} env
 * @returns {BadgeTone}
 */
export function cloudEnvironmentTone(env) {
  switch (String(env || '').toLowerCase()) {
    case 'gcc':      return 'info';
    case 'gcchigh':  return 'warning';
    case 'dod':      return 'critical';
    default:         return 'neutral';
  }
}

/**
 * Event type (timeline / alerts). Currently unused outside `TimelineView`,
 * which defers its `getTypeBadge` migration (17 distinct hues don't collapse
 * cleanly to 6 tones — see PR notes). Exposed so future callers can map the
 * types the spec does cover.
 * @param {string} type
 * @returns {BadgeTone}
 */
export function eventTypeTone(type) {
  switch (type) {
    case 'SignIn':        return 'info';
    case 'Exchange':      return 'purple';
    case 'DLP':           return 'warning';
    case 'RiskDetection':
    case 'RiskyUser':
      return 'critical';
    case 'General':       return 'neutral';
    default:              return 'neutral';
  }
}

/**
 * App / IP category. Spec covers `'Microsoft' | 'ThirdParty'`; real code also
 * emits `'Custom'` (user-added entries) and `'Reserved'` (IANA/RFC1918 ranges).
 * @param {string} category
 * @returns {BadgeTone}
 */
export function appCategoryTone(category) {
  switch (category) {
    case 'Microsoft': return 'info';
    case 'ThirdParty': return 'purple';
    case 'Custom':    return 'warning';
    case 'Reserved':  return 'neutral';
    default:          return 'neutral';
  }
}

/**
 * Known-malicious-IP type — freeform string; suggested values from the spec are
 * c2 / phishing / scanner / tor_exit / known_proxy / malware_drop / exfil / unknown.
 * Active threats (c2 / malware_drop / exfil / phishing) → critical.
 * Reconnaissance / anonymizers (scanner / tor_exit / known_proxy) → warning.
 * @param {string} type
 * @returns {BadgeTone}
 */
export function ipMaliciousTypeTone(type) {
  switch (String(type || '').toLowerCase()) {
    case 'c2':
    case 'malware_drop':
    case 'exfil':
    case 'phishing':
      return 'critical';
    case 'scanner':
    case 'tor_exit':
    case 'known_proxy':
      return 'warning';
    default:
      return 'neutral';
  }
}

/**
 * File-hash algorithm — sha256 (strong) → info, sha1 → warning, md5 (weak) → neutral.
 * Purely informational; the pipeline compares all three of the alert's hashes.
 * @param {string} type
 * @returns {BadgeTone}
 */
export function hashTypeTone(type) {
  switch (String(type || '').toLowerCase()) {
    case 'sha256':
      return 'info';
    case 'sha1':
      return 'warning';
    case 'md5':
      return 'neutral';
    default:
      return 'neutral';
  }
}

/**
 * Intune compliance state: `'compliant' | 'noncompliant' | 'unknown' | 'error'`.
 * Used by the Device Posture panel on alerts.
 * @param {string} state
 * @returns {BadgeTone}
 */
export function complianceStateTone(state) {
  switch (String(state || '').toLowerCase()) {
    case 'compliant':    return 'success';
    case 'noncompliant': return 'critical';
    case 'error':        return 'critical';
    case 'unknown':      return 'warning';
    default:             return 'neutral';
  }
}

/**
 * Confidence: `'high' | 'medium' | 'low'` (baseline, IP inference, AI rule builder).
 * @param {string} confidence
 * @returns {BadgeTone}
 */
export function confidenceTone(confidence) {
  switch (String(confidence || '').toLowerCase()) {
    case 'high':   return 'success';
    case 'medium': return 'warning';
    case 'low':    return 'critical';
    default:       return 'neutral';
  }
}

/**
 * Mailbox-pair allowlist actor type. v1.60.0.
 * `*` is the cross-type wildcard ("any actor of any type" — shared-mailbox shape);
 * mapped to warning so it visually stands out from the type-specific values.
 * @param {string} actorType
 * @returns {BadgeTone}
 */
export function actorTypeTone(actorType) {
  switch (String(actorType || '').toLowerCase()) {
    case 'upn':         return 'info';
    case 'app_id':      return 'purple';
    case 'app_name':    return 'neutral';
    case 'service_sid': return 'purple';
    case '*':           return 'warning';
    default:            return 'neutral';
  }
}

/**
 * Portal activity-log category. v1.91.0.
 * Buckets the central audit log's `category` field into the six badge tones.
 * @param {string} category
 * @returns {BadgeTone}
 */
export function activityCategoryTone(category) {
  switch (String(category || '').toLowerCase()) {
    case 'tenant':     return 'info';
    case 'rule':       return 'purple';
    case 'allowlist':  return 'success';
    case 'ticketing':  return 'warning';
    case 'msp-config': return 'warning';
    case 'alert':      return 'critical';
    case 'case':       return 'info';
    case 'bpc':        return 'neutral';
    case 'ip-intel':   return 'purple';
    case 'sensitive':  return 'warning';
    case 'phi':        return 'warning';
    case 'job':        return 'neutral';
    default:           return 'neutral';
  }
}

/**
 * SLO health status. Backend returns `'ok' | 'warn' | 'breach' | 'no-data' | 'scheduled'`.
 * `scheduled` is for daily-cadence workloads that emit only ~1 blob/day — they
 * usually have zero samples in the last hour, which is normal cadence.
 * @param {string} status
 * @returns {BadgeTone}
 */
export function sloStatusTone(status) {
  switch (String(status || '').toLowerCase()) {
    case 'ok':        return 'success';
    case 'warn':      return 'warning';
    case 'breach':    return 'critical';
    case 'no-data':   return 'neutral';
    case 'scheduled': return 'info';
    default:          return 'neutral';
  }
}

/**
 * Cost fact-row state (Cost dashboard). A per-tenant/day cost row freezes
 * (`is_final=true`) after the ~3-day settle window once its $ is present — then it's
 * settled and auditable (success). Otherwise it's still recomputing / awaiting the
 * cost feed ("cost pending"), mapped to warning.
 * @param {boolean} isFinal
 * @returns {BadgeTone}
 */
export function costStateTone(isFinal) {
  return isFinal ? 'success' : 'warning';
}
